/**
 * Store locator SKU filter + viewport-driven zoom/pan/list.
 *
 * Two behaviors live here on top of the compiled map bundle:
 *   1. SKU/model search: reads SKUs from data-skus (Liquid) and filters list + map.
 *   2. Location search + viewport reflow: the map viewport is the single source of
 *      truth. A location search fits the camera to the searched zone's real Google
 *      bounds (no fixed zoom cap), and on every settle (idle) the store list and
 *      markers are recomputed from whatever is currently inside the viewport,
 *      sorted by the active search point (or map center when there's no search).
 */
(function () {
  const SELECTORS = {
    search: 'map-search',
    tab: '[data-search-tab]',
    panel: '[data-search-panel]',
    skuInput: '.map-place-list__search-input-sku',
    locationInput: '.map-place-list__search-input:not(.map-place-list__search-input-sku)',
    skuSubmit: '.map-place-list__search-sku-submit',
    skuClear: '.map-place-list__search-sku-clear',
    listStore: '[map-target]',
  };

  const MAP_API_MAX_WAIT_MS = 10000;
  const MAP_API_POLL_MS = 100;
  const MAP_STORES_MAX_WAIT_MS = 15000;
  const STORE_ZOOM_LEVEL = 16;
  // Only used as a fallback zoom for point-only searches (geolocation, or when
  // Google can't return an area for the query). Area searches ignore it entirely.
  const DEFAULT_SEARCH_ZOOM_LEVEL = 13;
  const FALLBACK_DEFAULT_CENTER = { lat: 40.4168, lng: -3.7038 };
  const FALLBACK_DEFAULT_ZOOM = 10;
  const SEARCH_FIT_BOUNDS_PADDING = 48;
  const VIEWPORT_REFLOW_DEBOUNCE_MS = 500;
  const MARKER_CREATE_BATCH_SIZE = 80;
  const SKU_SPLIT_RE = /[,;•·|]/;

  function getStoreRegistry(mapId) {
    return mapId ? window.__mapPlaceListRegistry?.[mapId] || null : null;
  }

  function isLocationZoomDebugEnabled() {
    if (typeof window === 'undefined') return false;
    if (window.__mapPlaceListDebugZoom === true) return true;
    try {
      return window.localStorage?.getItem('mapPlaceListDebugZoom') === '1';
    } catch {
      return false;
    }
  }

  function debugLocationZoom(label, payload) {
    if (!isLocationZoomDebugEnabled()) return;
    if (payload !== undefined) {
      console.log('[map-place-list-zoom]', label, payload);
    } else {
      console.log('[map-place-list-zoom]', label);
    }
  }

  function getMapViewSnapshot(viewer) {
    if (!viewer?.map) return { mapReady: false };

    const center = viewer.map.getCenter();
    return {
      mapReady: true,
      zoom: viewer.map.getZoom(),
      center: center ? { lat: center.lat(), lng: center.lng() } : null,
      locationSearchActive: Boolean(viewer._locationSearchActive),
    };
  }

  function describeBounds(bounds) {
    if (!bounds?.getNorthEast) return null;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return {
      ne: { lat: ne.lat(), lng: ne.lng() },
      sw: { lat: sw.lat(), lng: sw.lng() },
      latSpan: Math.abs(ne.lat() - sw.lat()),
      lngSpan: Math.abs(ne.lng() - sw.lng()),
    };
  }

  function cssEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(String(value));
    }

    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function getMapId(root) {
    return root && root.getAttribute('map-id');
  }

  function switchTab(root, tabName) {
    root.querySelectorAll(SELECTORS.tab).forEach((tab) => {
      const isActive = tab.getAttribute('data-search-tab') === tabName;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.classList.toggle('map-place-list__search-tab--active', isActive);
    });

    root.querySelectorAll(SELECTORS.panel).forEach((panel) => {
      const isActive = panel.getAttribute('data-search-panel') === tabName;
      panel.classList.toggle('hidden', !isActive);
      panel.toggleAttribute('hidden', !isActive);
    });
  }

  function showNotification(message) {
    if (!message) return;
    document.dispatchEvent(
      new CustomEvent('simple-notification:show', {
        bubbles: true,
        cancelable: true,
        detail: { status: 'error', message },
      })
    );
  }

  function getErrorTemplate(root, name) {
    const template = root.querySelector(`template[name="${name}"]`);
    return template ? template.content.firstElementChild.cloneNode(true).outerHTML : '';
  }

  function parseSkusFromElement(el) {
    if (!el) return [];

    const json = el.getAttribute('data-skus-json');
    if (json) {
      try {
        const parsed = JSON.parse(json);
        if (Array.isArray(parsed)) {
          return parsed.map((sku) => String(sku).trim().toLowerCase()).filter(Boolean);
        }
      } catch {
        // Fall back to CSV attribute when JSON is invalid.
      }
    }

    const csv = el.getAttribute('data-skus') || '';
    return csv.split(SKU_SPLIT_RE).map((sku) => sku.trim().toLowerCase()).filter(Boolean);
  }

  function getStoreListElements(mapId) {
    const escapedMapId = cssEscape(mapId);
    const selector = [
      `map-controller[map-id="${escapedMapId}"] ${SELECTORS.listStore}`,
      `map-list-drawer[map-id="${escapedMapId}"] ${SELECTORS.listStore}`,
    ].join(', ');

    return document.querySelectorAll(selector);
  }

  function getStoreSkus(mapId, storeName) {
    const skus = new Set();
    const escapedMapId = cssEscape(mapId);
    const escapedStoreName = cssEscape(storeName);

    document
      .querySelectorAll(
        `map-controller[map-id="${escapedMapId}"] ${SELECTORS.listStore}[map-target="${escapedStoreName}"]`
      )
      .forEach((el) => parseSkusFromElement(el).forEach((sku) => skus.add(sku)));

    document
      .querySelectorAll(`map-list-drawer[map-id="${escapedMapId}"] ${SELECTORS.listStore}[map-target="${escapedStoreName}"]`)
      .forEach((el) => parseSkusFromElement(el).forEach((sku) => skus.add(sku)));

    document
      .querySelectorAll(`map-viewer[map-id="${escapedMapId}"] template[name="${escapedStoreName}"]`)
      .forEach((el) => parseSkusFromElement(el).forEach((sku) => skus.add(sku)));

    return [...skus];
  }

  function storeHasSku(mapId, storeName, sku) {
    const normalized = sku.trim().toLowerCase();
    if (!normalized) return true;

    const registry = getStoreRegistry(mapId);
    if (registry) {
      const entry = registry.byName.get(storeName);
      return entry ? entry.skuSet.has(normalized) : false;
    }

    return getStoreSkus(mapId, storeName).includes(normalized);
  }

  function getAllStoreNames(mapId) {
    const registry = getStoreRegistry(mapId);
    if (registry?.allNames.length) return registry.allNames;

    const names = new Set();
    const escapedMapId = cssEscape(mapId);

    document.querySelectorAll(`map-viewer[map-id="${escapedMapId}"] template[name^="store_"]`).forEach((el) => {
      const name = el.getAttribute('name');
      if (name) names.add(name);
    });

    getStoreListElements(mapId).forEach((el) => {
      const name = el.getAttribute('map-target');
      if (name) names.add(name);
    });

    return [...names];
  }

  function isLatLngInsideBounds(lat, lng, bounds) {
    if (!bounds?.getNorthEast) return false;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return lat <= ne.lat() && lat >= sw.lat() && lng <= ne.lng() && lng >= sw.lng();
  }

  function filterStoreListDom(mapId, storeNames) {
    const order = Array.isArray(storeNames) ? storeNames : null;
    const allowed = order ? new Set(order) : null;
    const signature = order ? order.join('|') : '';
    const listState = window.__mapPlaceListListState || (window.__mapPlaceListListState = {});

    if (listState[mapId] === signature) return;
    listState[mapId] = signature;

    const elementsByContainer = new Map();

    getStoreListElements(mapId).forEach((el) => {
      const name = el.getAttribute('map-target');
      const visible = !allowed || (name && allowed.has(name));
      el.classList.toggle('hidden', !visible);

      if (!order || !visible || !el.parentElement) return;

      if (!elementsByContainer.has(el.parentElement)) {
        elementsByContainer.set(el.parentElement, new Map());
      }
      elementsByContainer.get(el.parentElement).set(name, el);
    });

    if (!order?.length) return;

    elementsByContainer.forEach((visibleElements, container) => {
      const fragment = document.createDocumentFragment();
      order.forEach((name) => {
        const el = visibleElements.get(name);
        if (el) fragment.appendChild(el);
      });
      container.appendChild(fragment);
    });
  }

  function getMapViewer(mapId) {
    return document.querySelector(`map-viewer[map-id="${cssEscape(mapId)}"]`);
  }

  function getStoreCoords(mapId, storeName, storeEl) {
    const lat = parseFloat(
      storeEl?.getAttribute('data-latitude') || storeEl?.getAttribute('latitude')
    );
    const lng = parseFloat(
      storeEl?.getAttribute('data-longitude') || storeEl?.getAttribute('longitude')
    );

    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return { lat, lng };
    }

    const place = getMapViewer(mapId)?.places?.get(storeName);

    if (place) {
      const placeLat = parseFloat(place.latitude);
      const placeLng = parseFloat(place.longitude);
      if (!Number.isNaN(placeLat) && !Number.isNaN(placeLng)) {
        return { lat: placeLat, lng: placeLng };
      }

      if (place.location) {
        return { lat: place.location.lat(), lng: place.location.lng() };
      }
    }

    return null;
  }

  function getStoreCoordsByName(mapId, storeName) {
    const registry = getStoreRegistry(mapId);
    if (registry) {
      const entry = registry.byName.get(storeName);
      if (entry && !Number.isNaN(entry.lat) && !Number.isNaN(entry.lng)) {
        return { lat: entry.lat, lng: entry.lng };
      }
    }

    const escapedMapId = cssEscape(mapId);
    const escapedStoreName = cssEscape(storeName);

    const storeEl =
      document.querySelector(`map-controller[map-id="${escapedMapId}"] [map-target="${escapedStoreName}"]`) ||
      document.querySelector(`map-viewer[map-id="${escapedMapId}"] template[name="${escapedStoreName}"]`);

    return getStoreCoords(mapId, storeName, storeEl);
  }

  function buildPlaceFromTemplate(mapId, storeName) {
    const template = document.querySelector(
      `map-viewer[map-id="${cssEscape(mapId)}"] template[name="${cssEscape(storeName)}"]`
    );
    if (!template) return null;

    const node = template.content.firstElementChild.cloneNode(true);
    const latitude = template.getAttribute('latitude') || null;
    const longitude = template.getAttribute('longitude') || null;

    return {
      node,
      navigateButton: node.querySelector('[navigate-button]'),
      address: template.getAttribute('address') || '',
      longitude: longitude || null,
      latitude: latitude || null,
      name: storeName,
      skus: parseSkusFromElement(template),
    };
  }

  function countStoresMissingMarkers(viewer, mapId) {
    const storeNames = getAllStoreNames(mapId);
    if (!storeNames.length) return 0;

    return storeNames.filter((name) => {
      const place = viewer.places?.get(name);
      return !place?.marker;
    }).length;
  }

  function createMarkerForStore(viewer, mapId, storeName) {
    const existing = viewer.places?.get(storeName);
    if (existing?.marker) return existing.marker;

    const coords = getStoreCoordsByName(mapId, storeName);
    if (!coords) return null;

    const place = existing || buildPlaceFromTemplate(mapId, storeName);
    if (!place) return null;

    const position = new google.maps.LatLng(coords.lat, coords.lng);
    const marker =
      typeof viewer.addMarker === 'function'
        ? viewer.addMarker(position, { ...place, name: storeName })
        : new google.maps.Marker({ position, icon: viewer.storeIcon || undefined, map: viewer.map });

    if (!viewer.markers) viewer.markers = [];
    if (!viewer.markers.includes(marker)) {
      viewer.markers.push(marker);
      if (viewer.cluster && typeof viewer.cluster.addMarker === 'function') {
        viewer.cluster.addMarker(marker, true);
      }
    }

    viewer.places.set(storeName, { ...place, name: storeName, location: position, marker });
    return marker;
  }

  function ensureMarkersForStores(viewer, mapId, storeNames) {
    if (!viewer?.map || !Array.isArray(storeNames) || !storeNames.length) return 0;

    let created = 0;
    const pending = storeNames.filter((name) => !viewer.places?.get(name)?.marker);

    for (let i = 0; i < pending.length && created < MARKER_CREATE_BATCH_SIZE; i++) {
      if (createMarkerForStore(viewer, mapId, pending[i])) created++;
    }

    if (created > 0) {
      if (!viewer.cluster && typeof viewer.putInCluster === 'function') {
        viewer.putInCluster();
      } else {
        rerenderVisibleClusters(viewer);
      }
    }

    if (pending.length > created) {
      viewer._pendingMarkerStores = storeNames;
    }

    return created;
  }

  // Creates map markers from JSON/DOM coordinates without calling the Geocoder.
  // Markers are created lazily for the requested store set instead of all ~5000 at once.
  async function ensureMapMarkersReady(viewer, mapId, targetStoreNames) {
    if (!viewer?.map || typeof google === 'undefined' || !google.maps?.LatLng) return false;

    const storeNames = Array.isArray(targetStoreNames) && targetStoreNames.length
      ? targetStoreNames
      : getAllStoreNames(mapId);
    if (!storeNames.length) return false;

    if (typeof viewer.grabTemplates === 'function' && viewer.places.size < storeNames.length) {
      viewer.grabTemplates();
    }

    if (!viewer.markers) viewer.markers = [];

    const created = ensureMarkersForStores(viewer, mapId, storeNames);

    const activeSku = getActiveSkuForMap(mapId);
    const visibilityTargets =
      Array.isArray(targetStoreNames) && targetStoreNames.length
        ? targetStoreNames
        : activeSku
          ? getMatchedStoresForSku(mapId, activeSku)
          : null;

    if (!viewer.markers?.length && created === 0 && !visibilityTargets?.length) return false;

    if (visibilityTargets?.length) {
      viewer.updateMarkerVisibility(visibilityTargets);
    } else {
      viewer.updateMarkerVisibility(null);
    }

    if (shouldUseDefaultMapCenter(viewer, mapId) && !viewer._initialMapFitDone && !activeSku) {
      applyDefaultMapCenter(viewer);
    }

    debugLocationZoom('ensureMapMarkersReady', {
      mapId,
      created,
      requested: storeNames.length,
      totalMarkers: viewer.markers.length,
    });

    return true;
  }

  function scheduleEnsureMapMarkers(mapId, startedAt = Date.now()) {
    if (!mapId) return;

    const deadline = Date.now() + MAP_STORES_MAX_WAIT_MS;
    const waitForBundledInitMs = 1500;

    const trySync = () => {
      const viewer = getMapViewer(mapId);
      if (!viewer) {
        if (Date.now() < deadline) setTimeout(trySync, MAP_API_POLL_MS);
        return;
      }

      ensureMapViewerPatched(viewer);

      if (!viewer.map) {
        if (Date.now() < deadline) setTimeout(trySync, MAP_API_POLL_MS);
        return;
      }

      const storeNames = getAllStoreNames(mapId);
      if (!storeNames.length) {
        if (Date.now() < deadline) setTimeout(trySync, MAP_API_POLL_MS);
        return;
      }

      const targetStores = viewer._pendingMarkerStores;
      const bundledFinished = Boolean(viewer.cluster);
      const waitedLongEnough = Date.now() - startedAt >= waitForBundledInitMs;

      if (!bundledFinished && !waitedLongEnough && !viewer.places?.size && Date.now() < deadline) {
        setTimeout(trySync, MAP_API_POLL_MS);
        return;
      }

      if (!targetStores?.length && !viewer._initialReflowDone) {
        viewer._initialReflowDone = true;
        reflowViewportStores(viewer);
        viewer._markerSyncComplete = true;
        return;
      }

      if (!targetStores?.length) {
        viewer._markerSyncComplete = true;
        return;
      }

      ensureMapMarkersReady(viewer, mapId, targetStores).finally(() => {
        viewer._markerSyncComplete = true;
        viewer._pendingMarkerStores = null;

        const stillMissing = targetStores.some((name) => !viewer.places?.get(name)?.marker);
        if (stillMissing && Date.now() < deadline) {
          viewer._pendingMarkerStores = targetStores;
          setTimeout(trySync, MAP_API_POLL_MS);
        }
      });
    };

    trySync();
  }

  // Haversine distance in meters. Used to sort the visible store list by distance
  // to the active search point (or map center when there's no active search).
  function haversineMeters(lat1, lng1, lat2, lng2) {
    const toRad = (value) => (value * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 6371008.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function resolveLatLng(location) {
    if (!location) return null;
    const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
    const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
    return typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng)
      ? { lat, lng }
      : null;
  }

  function getLocationSearchQuery(mapId) {
    if (!mapId) return '';

    const escapedMapId = cssEscape(mapId);
    const input = document.querySelector(
      `${SELECTORS.search}[map-id="${escapedMapId}"] ${SELECTORS.locationInput}`
    );
    return input?.value.trim() || '';
  }

  // Country restriction, derived from the section's google-api-region attribute
  // (e.g. "es-ES" -> "es"). Mirrors the bundle's own geocode() request so our
  // bounds lookup resolves the query to the same country as the bundle.
  function getRegionCountry(mapId) {
    const region = getMapViewer(mapId)?.getAttribute('google-api-region') || '';
    const country = region.split('-').pop();
    return country ? country.toLowerCase() : '';
  }

  let sharedGeocoder = null;
  function getGeocoder() {
    if (typeof google === 'undefined' || !google.maps?.Geocoder) return null;
    if (!sharedGeocoder) sharedGeocoder = new google.maps.Geocoder();
    return sharedGeocoder;
  }

  // Recovers the real extent of the searched zone from Google. The compiled map
  // bundle only forwards a single point in the map-place-list:search event and
  // discards the geometry, so we geocode the query ourselves and read the area
  // bounds. Prefer geometry.bounds (the true zone extent, e.g. a whole state or a
  // zipcode's few km) over geometry.viewport (Google's padded recommended view).
  // Returns a Promise resolving to a google.maps.LatLngBounds, or null when the
  // query only resolves to a point (address/establishment with no area).
  function geocodeSearchBounds(mapId, query) {
    const geocoder = getGeocoder();
    if (!geocoder || !query) return Promise.resolve(null);

    const request = { address: query };
    const country = getRegionCountry(mapId);
    if (country) request.componentRestrictions = { country };

    return geocoder
      .geocode(request)
      .then(({ results }) => {
        const geometry = results?.[0]?.geometry;
        if (!geometry) return null;
        const bounds = geometry.bounds || geometry.viewport || null;
        debugLocationZoom('geocodeSearchBounds: resolved', {
          query,
          usedBounds: Boolean(geometry.bounds),
          bounds: describeBounds(bounds),
        });
        return bounds;
      })
      .catch((error) => {
        debugLocationZoom('geocodeSearchBounds: failed', { query, error: String(error) });
        return null;
      });
  }

  function getSearchZoomLevel(viewer) {
    const zoom = parseInt(viewer?.getAttribute('search-zoom-level'), 10);
    return Number.isFinite(zoom) && zoom > 0 ? zoom : DEFAULT_SEARCH_ZOOM_LEVEL;
  }

  function getDefaultMapView(viewer) {
    const lat = parseFloat(viewer?.getAttribute('default-center-lat'));
    const lng = parseFloat(viewer?.getAttribute('default-center-lng'));
    const zoom = parseInt(viewer?.getAttribute('default-zoom-level'), 10);

    return {
      lat: Number.isFinite(lat) ? lat : FALLBACK_DEFAULT_CENTER.lat,
      lng: Number.isFinite(lng) ? lng : FALLBACK_DEFAULT_CENTER.lng,
      zoom: Number.isFinite(zoom) && zoom > 0 ? zoom : FALLBACK_DEFAULT_ZOOM,
    };
  }

  function applyDefaultMapCenter(viewer) {
    if (!viewer?.map) return false;

    const { lat, lng, zoom } = getDefaultMapView(viewer);
    viewer.map.setCenter({ lat, lng });
    viewer.map.setZoom(zoom);
    viewer._initialMapFitDone = true;

    debugLocationZoom('applyDefaultMapCenter', { lat, lng, zoom, ...getMapViewSnapshot(viewer) });
    return true;
  }

  function shouldUseDefaultMapCenter(viewer, mapId) {
    if (!viewer?.map || !mapId) return false;
    if (viewer._locationSearchActive) return false;
    if (getActiveSkuForMap(mapId)) return false;
    return true;
  }

  function callMapFitBounds(viewer, bounds, padding) {
    const map = viewer?.map;
    if (!map || !bounds) return false;

    debugLocationZoom('callMapFitBounds', {
      bounds: describeBounds(bounds),
      padding: padding ?? SEARCH_FIT_BOUNDS_PADDING,
      before: getMapViewSnapshot(viewer),
    });
    map.fitBounds(bounds, padding ?? SEARCH_FIT_BOUNDS_PADDING);
    return true;
  }

  function updateSearchLocationMarker(viewer, location) {
    if (!viewer?.map || !location || typeof google === 'undefined' || !google.maps?.Marker) return;

    const point = resolveLatLng(location);
    if (!point) return;

    if (viewer.marker) viewer.marker.setMap(null);

    viewer.marker = new google.maps.Marker({
      position: point,
      icon: viewer.locationIcon,
      map: viewer.map,
    });
  }

  function clearSearchLocationMarker(viewer) {
    if (viewer?.marker) {
      viewer.marker.setMap(null);
      viewer.marker = null;
    }
  }

  function clearListStoreSelection(mapId) {
    getStoreListElements(mapId).forEach((el) => el.removeAttribute('selected'));
  }

  // Applies a resolved location-search fit to the map. `fit` is either { viewport }
  // (an area's LatLngBounds) or { point } (a single coordinate). A token guards
  // against stale async results: a newer search or a user gesture invalidates it.
  // If the map isn't ready yet, the fit is stashed and replayed on API load.
  function applyLocationFit(viewer, fit, token) {
    if (!viewer || viewer._locationFitToken !== token) return;

    if (!viewer.map) {
      viewer._pendingLocationFit = { fit, token };
      return;
    }

    if (fit.viewport) {
      viewer._lastSearchViewport = fit.viewport;
      debugLocationZoom('applyLocationFit: fit to zone bounds', {
        bounds: describeBounds(fit.viewport),
        before: getMapViewSnapshot(viewer),
      });
      callMapFitBounds(viewer, fit.viewport);
      return;
    }

    if (fit.point) {
      viewer._lastSearchViewport = null;
      debugLocationZoom('applyLocationFit: center on point (no area)', {
        point: fit.point,
        zoom: getSearchZoomLevel(viewer),
      });
      viewer.map.setCenter(fit.point);
      viewer.map.setZoom(getSearchZoomLevel(viewer));
    }
  }

  // Runs a location search: fit the map to the searched zone's Google bounds (no
  // zoom cap), or center on the point at the fallback zoom for point-only searches
  // (geolocation / addresses with no area). The list + markers are NOT set here;
  // they follow from the viewport reflow that fires on the resulting map 'idle'.
  function runLocationSearch(viewer, mapId, location) {
    if (!viewer) return;

    const point = resolveLatLng(location);
    viewer._lastLocationSearchPoint = point;
    viewer._locationSearchActive = true;

    const token = (viewer._locationFitToken = (viewer._locationFitToken || 0) + 1);

    if (viewer.infowindow) viewer.infowindow.close();
    clearListStoreSelection(mapId);
    updateSearchLocationMarker(viewer, location);

    const query = getLocationSearchQuery(mapId);
    debugLocationZoom('runLocationSearch', {
      mapId,
      query,
      point,
      token,
      before: getMapViewSnapshot(viewer),
    });

    // Geolocation (and any search with no query text) has no "zone" to fit — just
    // center on the point at the configured fallback zoom.
    if (!query) {
      if (point) applyLocationFit(viewer, { point }, token);
      return;
    }

    geocodeSearchBounds(mapId, query)
      .then((viewport) => {
        if (viewer._locationFitToken !== token) return;
        if (viewport) applyLocationFit(viewer, { viewport }, token);
        else if (point) applyLocationFit(viewer, { point }, token);
      })
      .catch(() => {
        if (viewer._locationFitToken !== token) return;
        if (point) applyLocationFit(viewer, { point }, token);
      });
  }

  function clearLocationSearchState(viewer) {
    if (!viewer) return;
    viewer._locationSearchActive = false;
    viewer._lastLocationSearchPoint = null;
    viewer._lastSearchViewport = null;
    viewer._pendingLocationFit = null;
    viewer._locationFitToken = (viewer._locationFitToken || 0) + 1;
    clearSearchLocationMarker(viewer);

    const mapId = viewer.getAttribute('map-id');
    if (mapId && shouldUseDefaultMapCenter(viewer, mapId)) {
      applyDefaultMapCenter(viewer);
      viewer._lastVisibleStoresSignature = null;
      reflowViewportStores(viewer);
    }
  }

  function handleLocationSearchEvent(event) {
    const detail = event.detail || {};
    const { mapId, location } = detail;
    if (!mapId || !location) return;

    resetSkuInputForMap(mapId);

    const viewer = ensureMapViewerPatched(getMapViewer(mapId));
    if (!viewer) return;

    runLocationSearch(viewer, mapId, location);

    // Stop the bundled displaySearch() listener — it schedules a 100ms fitBounds on
    // the geocoded point + closest store, which fights our zone-bounds fit.
    event.stopImmediatePropagation();
  }

  // The bundled map-viewer subscribes to filter state with selectBlock.bind(this) in
  // connectedCallback — before this script runs — so instance patches never intercept
  // the original selectBlock (which force-set zoom to 12). Guard setZoom so a SKU
  // search can block the bundle from snapping back to zoom 12.
  function ensureMapZoomGuard(viewer) {
    if (!viewer?.map || viewer._mapZoomGuardApplied) return;

    viewer._mapZoomGuardApplied = true;
    const nativeSetZoom = viewer.map.setZoom.bind(viewer.map);

    viewer.map.setZoom = function guardedSetZoom(zoom) {
      const blocked = viewer._blockBundleSetZoom && zoom === 12;
      if (blocked) return viewer.map;
      return nativeSetZoom(zoom);
    };
  }

  function patchMapPlaceListZoom(api) {
    if (!api || api._locationZoomPatchApplied) return api;

    api._locationZoomPatchApplied = true;

    const originalSetFilteredStores = api.setFilteredStores.bind(api);

    api.setFilteredStores = function setFilteredStoresPatched(mapId, stores, sku) {
      const activeSku = sku && String(sku).trim();

      // Location searches never route through the bundle's shared filter state
      // (which auto-selects the first store and force-zooms). Only SKU filtering
      // uses it; without a SKU just reflect the given list in the DOM.
      if (!activeSku) {
        filterStoreListDom(mapId, Array.isArray(stores) ? stores : null);
        return stores;
      }

      const viewer = getMapViewer(mapId);
      if (viewer?.map) ensureMapZoomGuard(viewer);
      if (viewer) viewer._blockBundleSetZoom = true;

      const matchedStores = originalSetFilteredStores(mapId, stores, sku);

      if (viewer) viewer._blockBundleSetZoom = false;

      return matchedStores;
    };

    return api;
  }

  // Invalidates any pending "fit to searched zone" that hasn't landed yet, so a real
  // user gesture always wins over a late-resolving geocode/fit.
  function cancelPendingSearchZoom(viewer) {
    viewer._locationFitToken = (viewer._locationFitToken || 0) + 1;
    viewer._pendingLocationFit = null;
  }

  // Native DOM gesture events (drag, touch, wheel — including clicks on the map's
  // own zoom controls, which live inside the map div) only ever originate from real
  // user input; our own setCenter/setZoom/panTo/fitBounds calls never dispatch them.
  // Use them as an unambiguous "the user just took control" signal.
  function ensureUserGestureCancelWired(viewer) {
    if (!viewer?.map || viewer.dataset.userGestureCancelWired === 'true') return;

    const mapDiv = viewer.map.getDiv && viewer.map.getDiv();
    if (!mapDiv) return;

    viewer.dataset.userGestureCancelWired = 'true';

    ['mousedown', 'touchstart', 'wheel'].forEach((type) => {
      mapDiv.addEventListener(type, () => cancelPendingSearchZoom(viewer), { passive: true });
    });
  }

  // The single source of truth for the list + markers: recompute which stores fall
  // inside the map's current viewport and show exactly those. Runs on every map
  // 'idle' (after a search fit, a pan, or a zoom). When a SKU filter is active the
  // candidate set is limited to SKU-matching stores; otherwise every store is a
  // candidate. Sorted by distance to the active search point, or the map center
  // when there's no active search.
  //
  // Deliberately does NOT go through setFilteredStores()/the bundle's shared Valtio
  // state: every write there marks the first result "selected", which force-zooms
  // the map, opens an info window, and scrolls the sidebar list. This only touches
  // updateMarkerVisibility() (toggles marker.setVisible() + re-renders the clusterer)
  // and our own DOM-only list helper, so it never changes the zoom or steals focus.
  function reflowViewportStores(viewer) {
    if (!viewer?.map || typeof viewer.updateMarkerVisibility !== 'function') return;

    const mapId = viewer.getAttribute('map-id');
    const bounds = viewer.map.getBounds();
    if (!mapId || !bounds) return;

    const activeSku = viewer._locationSearchActive ? '' : getActiveSkuForMap(mapId);
    let candidateNames;

    if (activeSku) {
      const registry = getStoreRegistry(mapId);
      const normalizedSku = activeSku.trim().toLowerCase();
      candidateNames = registry?.skuToStores.has(normalizedSku)
        ? [...registry.skuToStores.get(normalizedSku)]
        : getAllStoreNames(mapId).filter((name) => storeHasSku(mapId, name, activeSku));
    } else {
      candidateNames = getAllStoreNames(mapId);
    }

    const searchPoint = viewer._lastLocationSearchPoint || null;
    const center = viewer.map.getCenter();
    const sortPoint = searchPoint || (center ? { lat: center.lat(), lng: center.lng() } : null);

    const visibleStores = [];
    for (const name of candidateNames) {
      const coords = getStoreCoordsByName(mapId, name);
      if (!coords) continue;

      // SKU search shows every matching store on the map, regardless of viewport.
      if (!activeSku && !isLatLngInsideBounds(coords.lat, coords.lng, bounds)) continue;

      visibleStores.push({
        name,
        distance: sortPoint
          ? haversineMeters(sortPoint.lat, sortPoint.lng, coords.lat, coords.lng)
          : 0,
      });
    }

    visibleStores.sort((a, b) => a.distance - b.distance);
    const orderedVisibleStores = visibleStores.map(({ name }) => name);
    const signature = `${activeSku || ''}:${orderedVisibleStores.join('|')}`;

    if (viewer._lastVisibleStoresSignature === signature) return;
    viewer._lastVisibleStoresSignature = signature;

    if (
      orderedVisibleStores.length === 0 &&
      candidateNames.length > 0 &&
      !activeSku &&
      shouldUseDefaultMapCenter(viewer, mapId)
    ) {
      if (!viewer._initialMapFitDone && applyDefaultMapCenter(viewer)) {
        viewer.updateMarkerVisibility(null);
        filterStoreListDom(mapId, null);
        debugLocationZoom('reflowViewportStores: applied default center', getMapViewSnapshot(viewer));
        return;
      }
    }

    ensureMarkersForStores(viewer, mapId, orderedVisibleStores);
    viewer.updateMarkerVisibility(orderedVisibleStores);

    // List order for SKU searches is owned by applySkuFilterToMap, not viewport reflow.
    if (!activeSku) {
      filterStoreListDom(mapId, orderedVisibleStores);
    }

    if (orderedVisibleStores.length && countStoresMissingMarkers(viewer, mapId) > 0) {
      viewer._pendingMarkerStores = orderedVisibleStores;
      scheduleEnsureMapMarkers(mapId);
    }

    debugLocationZoom('reflowViewportStores', {
      visibleCount: orderedVisibleStores.length,
      sortedBy: searchPoint ? 'searchPoint' : 'mapCenter',
      activeSku: activeSku || null,
      ...getMapViewSnapshot(viewer),
    });
  }

  // Wires the viewport reflow to the map's own 'idle' event (fires once panning/
  // zooming settles). Retries until viewer.map exists, since Google Maps loads async.
  function ensureViewportReflowWired(viewer, deadline) {
    if (!viewer || viewer.dataset.viewportReflowWired === 'true') return;

    if (!viewer.map) {
      const limit = deadline || Date.now() + MAP_API_MAX_WAIT_MS;
      if (Date.now() < limit) {
        setTimeout(() => ensureViewportReflowWired(viewer, limit), MAP_API_POLL_MS);
      }
      return;
    }

    viewer.dataset.viewportReflowWired = 'true';

    ensureUserGestureCancelWired(viewer);

    google.maps.event.addListener(viewer.map, 'idle', () => {
      clearTimeout(viewer._viewportReflowTimeout);
      viewer._viewportReflowTimeout = setTimeout(() => reflowViewportStores(viewer), VIEWPORT_REFLOW_DEBOUNCE_MS);
    });
  }

  function cleanupEmptyClusterMarkers(cluster) {
    (cluster.clusters || []).forEach((item) => {
      const marker = item?.marker;
      if (!marker) return;

      const markers = item.markers || [];
      const visibleCount = markers.filter((m) => m.getVisible && m.getVisible()).length;

      if (visibleCount === 0) {
        marker.setMap(null);
        if (typeof marker.setVisible === 'function') marker.setVisible(false);
        return;
      }

      if (markers.length > 1 && typeof marker.setLabel === 'function') {
        const label = marker.getLabel();
        if (label?.text && String(label.text) !== String(visibleCount)) {
          marker.setLabel({ ...label, text: String(visibleCount) });
        }
      }
    });
  }

  function ensureClusterRenderPatched(viewer) {
    if (!viewer?.cluster || viewer.cluster._visibleOnlyRenderPatch) return Boolean(viewer?.cluster);

    const cluster = viewer.cluster;
    cluster._visibleOnlyRenderPatch = true;

    const originalRender = cluster.render.bind(cluster);

    cluster.render = function renderVisibleMarkersOnly() {
      const allMarkers = cluster.markers || [];
      const visibleMarkers = allMarkers.filter((marker) => !marker.getVisible || marker.getVisible());

      if (visibleMarkers.length === 0) {
        if (typeof cluster.reset === 'function') cluster.reset();
        return;
      }

      if (visibleMarkers.length === allMarkers.length) {
        originalRender();
        cleanupEmptyClusterMarkers(cluster);
        return;
      }

      const savedMarkers = cluster.markers;
      cluster.markers = visibleMarkers;
      originalRender();
      cluster.markers = savedMarkers;
      cleanupEmptyClusterMarkers(cluster);
    };

    return true;
  }

  function rerenderVisibleClusters(viewer) {
    if (!viewer?.cluster) return;
    ensureClusterRenderPatched(viewer);
    viewer.cluster.render();
  }

  // Skip fitBoundsToFiltered on the next filter pass (list click only).
  // The map bundle refits all markers after every state change and overrides per-store focus.
  function ensureMapViewerPatched(viewer) {
    if (!viewer || viewer.dataset.storeFocusPatch === 'true') return viewer;

    viewer.dataset.storeFocusPatch = 'true';
    viewer._skipNextFitBounds = false;
    viewer._suppressSelectBlock = false;

    if (typeof viewer.fitBoundsToFiltered === 'function') {
      const originalFitBounds = viewer.fitBoundsToFiltered.bind(viewer);

      viewer.fitBoundsToFiltered = function fitBoundsToFilteredPatched(stores) {
        if (viewer._skipNextFitBounds) {
          viewer._skipNextFitBounds = false;
          return;
        }

        if (stores === null || stores === undefined) {
          return originalFitBounds(stores);
        }

        if (!Array.isArray(stores) || !stores.length) return;

        // The bundled fitBounds only uses Google marker locations, which aren't ready
        // during async init — an empty bounds fit zooms the map to 0,0. Prefer DOM coords.
        if (fitBoundsToStoreNames(viewer, stores)) {
          viewer._pendingFitBoundsStores = null;
          return;
        }

        viewer._pendingFitBoundsStores = stores;
        schedulePendingFitBounds(viewer);
      };
    }

    // focus() re-fits the map (initial load, and whenever a filter is cleared). Keep
    // the searched zone / SKU results instead of zooming back out to every store.
    if (typeof viewer.focus === 'function') {
      const originalFocus = viewer.focus.bind(viewer);

      viewer.focus = function focusPatched(...args) {
        const mapId = viewer.getAttribute('map-id');
        const activeSku = mapId && getActiveSkuForMap(mapId);

        // Initial map load always calls focus(); keep the SKU results in view when a
        // SKU search is active (e.g. auto-search from ?sku=).
        if (activeSku) {
          debugLocationZoom('focus(): SKU search active, re-fit matched stores', {
            activeSku,
            ...getMapViewSnapshot(viewer),
          });
          const matchedStores =
            viewer._pendingFitBoundsStores || getMatchedStoresForSku(mapId, activeSku);
          if (matchedStores.length) queueFitBoundsForStores(viewer, matchedStores);
          return;
        }

        // A location search owns the camera: re-apply the searched zone bounds (or
        // the search point) rather than zooming back out to all stores.
        if (viewer._locationSearchActive) {
          if (viewer._lastSearchViewport) {
            debugLocationZoom('focus(): location search active, re-fit zone bounds', {
              bounds: describeBounds(viewer._lastSearchViewport),
              ...getMapViewSnapshot(viewer),
            });
            callMapFitBounds(viewer, viewer._lastSearchViewport);
          } else if (viewer.map && viewer._lastLocationSearchPoint) {
            debugLocationZoom('focus(): location search active, center on point', {
              point: viewer._lastLocationSearchPoint,
              ...getMapViewSnapshot(viewer),
            });
            viewer.map.setCenter(viewer._lastLocationSearchPoint);
            viewer.map.setZoom(getSearchZoomLevel(viewer));
          }
          return;
        }

        if (applyDefaultMapCenter(viewer)) {
          debugLocationZoom('focus(): default center until search', getMapViewSnapshot(viewer));
          return;
        }

        debugLocationZoom('focus(): default bundle behavior', getMapViewSnapshot(viewer));
        return originalFocus(...args);
      };
    }

    // selectBlock() on the map viewer opens an info window and (in the bundle)
    // force-set zoom to 12. Instance patches do not intercept the bound subscriber
    // registered in connectedCallback; location searches bypass that path entirely.
    // This guard remains for explicit store picks (list click).
    if (typeof viewer.selectBlock === 'function') {
      viewer.selectBlock = function selectBlockPatched(payload) {
        if (viewer._suppressSelectBlock) return;

        const storeName = payload?.currentElement;
        const place = storeName && viewer.places?.get(storeName);

        if (!storeName || (!place?.location && !place?.marker)) {
          if (viewer._pendingFitBoundsStores) schedulePendingFitBounds(viewer);
          return;
        }

        if (typeof viewer.display === 'function') {
          viewer.display({ ...place, marker: place.marker });
        }
      };
    }

    // The map bundle's own findClosest() only matches stores that already have a
    // Google Maps marker, so it silently mismatches whenever markers aren't ready.
    // Route it through our location-search pipeline instead (fit to zone bounds).
    // In practice this is a safety net: handleLocationSearchEvent stops the bundle's
    // displaySearch() before it can call findClosest.
    if (typeof viewer.findClosest === 'function') {
      viewer.findClosest = function findClosestPatched(location) {
        const mapId = viewer.getAttribute('map-id');
        runLocationSearch(viewer, mapId, location);
        return null;
      };
    }

    // The bundled clusterer re-adds every marker on render(), ignoring setVisible(false).
    // Only cluster visible markers and drop clusters whose visible count is 0.
    if (typeof viewer.updateMarkerVisibility === 'function') {
      viewer.updateMarkerVisibility = function updateMarkerVisibilityPatched(storeNames) {
        const allowed =
          storeNames === null || storeNames === undefined
            ? null
            : Array.isArray(storeNames)
              ? new Set(storeNames)
              : null;

        this.places.forEach((place, name) => {
          const visible = allowed === null || (allowed.size > 0 && allowed.has(name));
          if (place.marker) place.marker.setVisible(visible);
        });

        rerenderVisibleClusters(this);
      };
    }

    ensureViewportReflowWired(viewer);

    return viewer;
  }

  function patchAllMapViewers(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('map-viewer').forEach(ensureMapViewerPatched);
  }

  // The sidebar list (map-controller) has its OWN selectBlock — separate from the
  // map viewer's — that highlights ("selected" attribute) and scrollIntoView()s the
  // nearest matched store every time the filtered-stores state changes. That's fine
  // for an explicit search, but every viewport reflow (i.e. every pan/zoom) also goes
  // through the same state update, so without this guard the list keeps auto-scrolling
  // to a "selected" store while the user is just moving the map around.
  function ensureListControllerPatched(controller) {
    if (!controller || controller.dataset.selectBlockPatch === 'true') return controller;
    if (typeof controller.selectBlock !== 'function') return controller;

    controller.dataset.selectBlockPatch = 'true';
    const originalSelectBlock = controller.selectBlock.bind(controller);

    controller.selectBlock = function selectBlockPatched(payload) {
      const mapId = controller.getAttribute('map-id');
      const viewer = mapId && getMapViewer(mapId);
      if (viewer && viewer._suppressSelectBlock) return;
      return originalSelectBlock(payload);
    };

    return controller;
  }

  function patchAllListControllers(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('map-controller').forEach(ensureListControllerPatched);
  }

  function openStoreOnMap(viewer, mapId, storeName, storeEl) {
    if (!viewer?.map) return;

    const place = viewer.places?.get(storeName);
    const coords = place?.location
      ? { lat: place.location.lat(), lng: place.location.lng() }
      : getStoreCoords(mapId, storeName, storeEl);

    if (!coords) return;

    if (place?.marker) {
      place.marker.setVisible(true);
    }

    viewer.map.setZoom(STORE_ZOOM_LEVEL);
    viewer.map.panTo(coords);

    const showStoreInfo = () => {
      if (viewer.cluster && typeof viewer.cluster.render === 'function') {
        viewer.cluster.render();
      }

      if (!place?.node || !viewer.infowindow) return;

      if (place.navigateButton && place.location) {
        place.navigateButton.href = `https://www.google.com/maps/search/?api=1&query=${place.location.lat()},${place.location.lng()}`;
      }

      viewer.infowindow.setContent(`\n   ${place.node.outerHTML}\n    `);

      const markerVisible = place.marker && place.marker.getVisible && place.marker.getVisible();

      if (markerVisible) {
        viewer.infowindow.open(viewer.map, place.marker);
        return;
      }

      viewer.infowindow.setPosition(coords);
      viewer.infowindow.open(viewer.map);
    };

    if (window.google?.maps?.event) {
      google.maps.event.addListenerOnce(viewer.map, 'idle', showStoreInfo);
    } else {
      setTimeout(showStoreInfo, 150);
    }
  }

  function handleStoreListClick(event) {
    const storeEl = event.target.closest(
      'map-controller [map-target], map-list-drawer [map-target]'
    );
    if (!storeEl || storeEl.classList.contains('hidden')) return;

    const listRoot = storeEl.closest('map-controller, map-list-drawer');
    const mapId = listRoot?.getAttribute('map-id');
    const storeName = storeEl.getAttribute('map-target');
    if (!mapId || !storeName) return;

    const viewer = ensureMapViewerPatched(getMapViewer(mapId));
    if (!viewer) return;

    viewer._skipNextFitBounds = true;

    whenMapPlaceListReady(() => {
      requestAnimationFrame(() => {
        openStoreOnMap(viewer, mapId, storeName, storeEl);
      });
    });
  }

  function getActiveSkuForMap(mapId) {
    if (!mapId) return '';

    const escapedMapId = cssEscape(mapId);
    const input = document.querySelector(`${SELECTORS.search}[map-id="${escapedMapId}"] ${SELECTORS.skuInput}`);
    const fromInput = input?.value.trim();
    if (fromInput) return fromInput;

    const params = new URLSearchParams(window.location.search);
    return params.get('sku')?.trim() || '';
  }

  function getMatchedStoresForSku(mapId, sku) {
    if (!mapId || !sku) return [];

    const normalized = sku.trim().toLowerCase();
    const registry = getStoreRegistry(mapId);
    if (registry?.skuToStores.has(normalized)) {
      return [...registry.skuToStores.get(normalized)];
    }

    return getAllStoreNames(mapId).filter((storeName) => storeHasSku(mapId, storeName, sku));
  }

  function whenMapPlaceListReady(callback, deadline) {
    const limit = deadline || Date.now() + MAP_API_MAX_WAIT_MS;

    if (window.__mapPlaceList) {
      callback(window.__mapPlaceList);
      return;
    }

    if (Date.now() >= limit) {
      callback(null);
      return;
    }

    setTimeout(() => whenMapPlaceListReady(callback, limit), MAP_API_POLL_MS);
  }

  // Waits until store templates and the map API helper exist before SKU search runs.
  function whenMapSearchReady(mapId, callback, deadline) {
    const limit = deadline || Date.now() + MAP_STORES_MAX_WAIT_MS;
    const mapApiReady = Boolean(window.__mapPlaceList);
    const storesReady = window.__mapPlaceListStoresReady === true;
    const storeNamesReady = mapId && getAllStoreNames(mapId).length > 0;
    const viewer = mapId && getMapViewer(mapId);

    if (mapApiReady && storesReady && storeNamesReady && viewer) {
      callback();
      return;
    }

    if (Date.now() >= limit) {
      callback();
      return;
    }

    setTimeout(() => whenMapSearchReady(mapId, callback, limit), MAP_API_POLL_MS);
  }

  // Fit map to stores using DOM lat/lng (available before Google markers finish loading).
  function fitBoundsToStoreNames(viewer, storeNames) {
    if (!viewer?.map || !Array.isArray(storeNames) || !storeNames.length) return false;
    if (typeof google === 'undefined' || !google.maps?.LatLng || !google.maps?.LatLngBounds) return false;

    const mapId = viewer.getAttribute('map-id');
    const bounds = new google.maps.LatLngBounds();
    let count = 0;

    storeNames.forEach((name) => {
      const coords = getStoreCoordsByName(mapId, name);
      if (!coords || Number.isNaN(coords.lat) || Number.isNaN(coords.lng)) return;
      bounds.extend(new google.maps.LatLng(coords.lat, coords.lng));
      count++;
    });

    if (!count) return false;

    return callMapFitBounds(viewer, bounds);
  }

  function schedulePendingFitBounds(viewer, deadline) {
    if (!viewer?._pendingFitBoundsStores?.length) return;

    const limit = deadline || Date.now() + MAP_STORES_MAX_WAIT_MS;

    const tryFit = () => {
      const pending = viewer._pendingFitBoundsStores;
      if (!pending?.length) return;

      if (fitBoundsToStoreNames(viewer, pending)) {
        viewer._pendingFitBoundsStores = null;
        return;
      }

      if (Date.now() < limit) {
        setTimeout(tryFit, MAP_API_POLL_MS);
      }
    };

    if (typeof google !== 'undefined' && google.maps) {
      tryFit();
      return;
    }

    if (Date.now() < limit) {
      setTimeout(() => schedulePendingFitBounds(viewer, limit), MAP_API_POLL_MS);
    }
  }

  function queueFitBoundsForStores(viewer, storeNames) {
    if (!viewer || !Array.isArray(storeNames) || !storeNames.length) return;

    if (fitBoundsToStoreNames(viewer, storeNames)) {
      viewer._pendingFitBoundsStores = null;
      return;
    }

    viewer._pendingFitBoundsStores = storeNames;
    schedulePendingFitBounds(viewer);
  }

  function reapplyUrlSkuSearch(mapId) {
    if (!mapId) return;

    const params = new URLSearchParams(window.location.search);
    const urlSku = params.get('sku');
    if (!urlSku) return;

    const escapedMapId = cssEscape(mapId);
    document.querySelectorAll(`${SELECTORS.search}[map-id="${escapedMapId}"]`).forEach((root) => {
      const input = root.querySelector(SELECTORS.skuInput);
      if (!input) return;

      if (input.value.trim().toLowerCase() !== urlSku.trim().toLowerCase()) {
        switchTab(root, 'model');
        input.value = urlSku;
      }

      applySkuFilter(root);
    });
  }

  function applySkuMapVisibility(viewer, mapId, matchedStores) {
    if (!viewer || !mapId || !Array.isArray(matchedStores)) return;

    viewer._lastVisibleStoresSignature = null;
    viewer._pendingMarkerStores = matchedStores;
    ensureMarkersForStores(viewer, mapId, matchedStores);
    viewer.updateMarkerVisibility(matchedStores);
    rerenderVisibleClusters(viewer);
  }

  function applySkuFilterToMap(mapId, sku, matchedStores) {
    if (!sku) {
      filterStoreListDom(mapId, null);
      const viewer = getMapViewer(mapId);
      if (viewer) viewer._lastVisibleStoresSignature = null;
      whenMapPlaceListReady((api) => {
        if (api && typeof api.clearSku === 'function') {
          api.clearSku(mapId);
        }
      });
      return;
    }

    filterStoreListDom(mapId, matchedStores);

    whenMapPlaceListReady((api) => {
      if (!api) return;

      const viewer = ensureMapViewerPatched(getMapViewer(mapId));
      if (viewer && matchedStores.length) {
        applySkuMapVisibility(viewer, mapId, matchedStores);
      }

      if (typeof api.setFilteredStores === 'function') {
        api.setFilteredStores(mapId, matchedStores, sku);
      } else if (typeof api.setSku === 'function') {
        api.setSku(mapId, sku);
      }

      if (viewer && matchedStores.length) {
        queueFitBoundsForStores(viewer, matchedStores);
      }
    });
  }

  function applySkuFilter(root) {
    const mapId = getMapId(root);
    const input = root.querySelector(SELECTORS.skuInput);
    if (!mapId || !input) return;

    const sku = input.value.trim();

    if (!sku) {
      applySkuFilterToMap(mapId, '', []);
      return;
    }

    resetLocationInputForMap(mapId);

    const matchedStores = getMatchedStoresForSku(mapId, sku);

    applySkuFilterToMap(mapId, sku, matchedStores);

    if (!matchedStores.length) {
      showNotification(getErrorTemplate(root, 'error-message-sku'));
    }
  }

  function resetLocationInputForMap(mapId) {
    const escapedMapId = cssEscape(mapId);
    document.querySelectorAll(`${SELECTORS.search}[map-id="${escapedMapId}"]`).forEach((root) => {
      const input = root.querySelector(SELECTORS.locationInput);
      if (input) input.value = '';
    });

    // A SKU search supersedes any active location search: clear the search point so
    // the viewport reflow sorts by map center again and the search marker is removed.
    clearLocationSearchState(getMapViewer(mapId));
  }

  function resetSkuInputForMap(mapId) {
    const escapedMapId = cssEscape(mapId);
    document.querySelectorAll(`${SELECTORS.search}[map-id="${escapedMapId}"]`).forEach((root) => {
      const input = root.querySelector(SELECTORS.skuInput);
      if (input) input.value = '';
    });
  }

  function clearSkuFilter(root) {
    const mapId = getMapId(root);
    const input = root.querySelector(SELECTORS.skuInput);
    if (input) input.value = '';
    if (!mapId) return;
    applySkuFilterToMap(mapId, '', []);
  }

  function initSearch(root) {
    if (root.dataset.skuSearchInit === 'true') return;
    root.dataset.skuSearchInit = 'true';

    const mapId = getMapId(root);

    root.querySelectorAll(SELECTORS.tab).forEach((tab) => {
      tab.addEventListener('click', () => {
        switchTab(root, tab.getAttribute('data-search-tab'));
      });
    });

    const skuClear = root.querySelector(SELECTORS.skuClear);
    if (skuClear) {
      skuClear.addEventListener('click', () => clearSkuFilter(root));
    }

    const skuInput = root.querySelector(SELECTORS.skuInput);
    if (skuInput) {
      skuInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          applySkuFilter(root);
        }
      });
    }

    const params = new URLSearchParams(window.location.search);
    const urlSku = params.get('sku');
    if (urlSku && mapId) {
      switchTab(root, 'model');
      skuInput.value = urlSku;
      whenMapSearchReady(mapId, () => applySkuFilter(root));
    }
  }

  function initAll() {
    whenMapPlaceListReady((api) => patchMapPlaceListZoom(api));
    patchAllMapViewers(document);
    patchAllListControllers(document);
    document.querySelectorAll(SELECTORS.search).forEach(initSearch);

    if (!document.documentElement.dataset.mapPlaceListSearchCapture) {
      document.documentElement.dataset.mapPlaceListSearchCapture = 'true';
      document.addEventListener('map-place-list:search', handleLocationSearchEvent, true);
    }
  }

  document.addEventListener(
    'click',
    (event) => {
      const submit = event.target.closest(SELECTORS.skuSubmit);
      if (submit) {
        const root = submit.closest(SELECTORS.search);
        if (!root) return;

        event.preventDefault();
        applySkuFilter(root);
      }
    },
    false
  );

  // Capture phase: set skip flag before the map bundle handles the click.
  document.addEventListener('click', handleStoreListClick, true);

  document.addEventListener('map-place-list:filtered', (event) => {
    const detail = event.detail || {};
    const { mapId, stores } = detail;
    if (!mapId) return;

    if (!Array.isArray(stores)) {
      filterStoreListDom(mapId, null);
      return;
    }

    filterStoreListDom(mapId, stores);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    whenMapPlaceListReady((api) => patchMapPlaceListZoom(api));
    patchAllMapViewers(event.target);
    patchAllListControllers(event.target);
    event.target.querySelectorAll(SELECTORS.search).forEach(initSearch);

    if (!document.documentElement.dataset.mapPlaceListSearchCapture) {
      document.documentElement.dataset.mapPlaceListSearchCapture = 'true';
      document.addEventListener('map-place-list:search', handleLocationSearchEvent, true);
    }
  });

  document.addEventListener('map-place-list:stores-loaded', (event) => {
    const mapId = event.detail?.mapId;
    if (!mapId) return;
    scheduleEnsureMapMarkers(mapId);
  });

  document.addEventListener('map-place-list:googlemaps-api-loaded', (event) => {
    const mapId = event.detail?.mapId;
    if (!mapId) return;

    scheduleEnsureMapMarkers(mapId);

    const viewer = ensureMapViewerPatched(getMapViewer(mapId));
    if (viewer?.map) ensureMapZoomGuard(viewer);

    if (viewer?._pendingFitBoundsStores?.length) {
      schedulePendingFitBounds(viewer);
    }

    if (viewer) {
      const clusterPatchDeadline = Date.now() + MAP_STORES_MAX_WAIT_MS;
      const patchClusterWhenReady = () => {
        if (ensureClusterRenderPatched(viewer)) return;
        if (Date.now() >= clusterPatchDeadline) return;
        setTimeout(patchClusterWhenReady, MAP_API_POLL_MS);
      };
      patchClusterWhenReady();
    }

    // Map init calls focus() on all stores after async marker loading. If the user
    // already searched before the map was ready, replay the fit to the searched zone
    // instead of leaving the map zoomed out to every store.
    if (viewer?._pendingLocationFit) {
      const pending = viewer._pendingLocationFit;
      viewer._pendingLocationFit = null;
      applyLocationFit(viewer, pending.fit, pending.token);
    } else if (viewer?._locationSearchActive && viewer._lastSearchViewport) {
      debugLocationZoom('googlemaps-api-loaded: replay searched zone fit', {
        mapId,
        bounds: describeBounds(viewer._lastSearchViewport),
        before: getMapViewSnapshot(viewer),
      });
      callMapFitBounds(viewer, viewer._lastSearchViewport);
    }

    reapplyUrlSkuSearch(mapId);
  });

  document.addEventListener('map-place-list:reset', (event) => {
    const detail = event.detail || {};
    if (detail.source === 'location' && detail.mapId) {
      resetSkuInputForMap(detail.mapId);
      clearLocationSearchState(getMapViewer(detail.mapId));
    }
  });
})();
